import { renderComment } from '@adornis/web-editor/client/comments/renderComment.js';
import { html } from 'lit';
import { CommentEditor } from '@adornis/web-editor/client/comments/CommentEditor.js';
import type { LandingEditorComment } from '../../shared/editor/landingEditorComments.js';
import { editorPages } from './editorPageConfig.js';
import { editorCommentAnchorExists } from './editorCommentOverlay.js';
import type { EditorCommentsState } from './editorCommentsState.js';

const pageLabel = (comment: LandingEditorComment) =>
  editorPages.find(page => page.key === comment.anchor.pageKey)?.label ?? comment.anchor.pageKey;

const timestamp = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).format(date);
};

export const renderEditorCommentComposer = (state: EditorCommentsState) => {
  if (!state.draft) return '';
  const style = `left:clamp(176px,${state.draft.left}px,calc(100vw - 176px));top:clamp(250px,${state.draft.top}px,calc(100vh - 16px));`;
  return html`
    <section class="vp-editor-comment-composer" style=${style} aria-label="Neuen Kommentar schreiben" @pointerdown=${(event: Event) => event.stopPropagation()}>
      <div class="vp-editor-comment-composer-head"><strong>Änderungswunsch</strong></div>
      ${CommentEditor({ id: `new:${state.draft.anchor.elementId}`, onSubmit: body => state.submitDraft(body), onCancel: state.cancelDraft })}
    </section>
  `;
};

export const renderEditorCommentPopover = (state: EditorCommentsState) => {
  const comment = state.activeComment;
  if (!comment || !state.popoverPoint) return '';
  const style = `left:clamp(160px,${state.popoverPoint.left}px,calc(100vw - 160px));top:clamp(180px,${state.popoverPoint.top}px,calc(100vh - 16px));`;
  return html`
    <section class="vp-editor-comment-popover" style=${style} aria-label="Kommentar">
      <div class="vp-editor-comment-popover-meta">
        <span>${pageLabel(comment)}</span><span>${timestamp(comment.updatedAt)}</span>
        <button type="button" aria-label="Kommentar schließen" @click=${state.closePopover}>
          <span class="icon-[material-symbols--close]"></span>
        </button>
      </div>
      ${renderComment(comment)}
      <div class="vp-editor-comment-popover-actions">
        <button type="button" @click=${() => void state.updateComment(comment, { status: 'resolved' })}>
          <span class="icon-[material-symbols--check-circle-outline]"></span> Erledigen
        </button>
        <button type="button" class="is-danger" aria-label="Kommentar löschen" @click=${() => void state.deleteComment(comment)}>
          <span class="icon-[material-symbols--delete-outline]"></span>
        </button>
      </div>
    </section>
  `;
};

export const renderEditorCommentsPanel = (
  state: EditorCommentsState,
  currentPageKey: string,
  currentAudience: string,
  anchorIds: Readonly<Record<string, string>>,
  onNavigate: (comment: LandingEditorComment) => void,
) => {
  const authors = [...new Set(state.comments.map(comment => comment.createdBy))];
  const filtered = state.comments
    .filter(comment => comment.status === state.statusFilter)
    .filter(comment => state.authorFilter === 'all' || comment.createdBy === state.authorFilter)
    .filter(comment =>
      state.scope === 'all' ||
      (comment.anchor.pageKey === currentPageKey && comment.anchor.audience === currentAudience),
    )
    .sort((left, right) => {
      const leftCurrent = left.anchor.pageKey === currentPageKey && left.anchor.audience === currentAudience;
      const rightCurrent = right.anchor.pageKey === currentPageKey && right.anchor.audience === currentAudience;
      return Number(rightCurrent) - Number(leftCurrent) || Date.parse(right.updatedAt) - Date.parse(left.updatedAt);
    });
  const openCount = state.comments.filter(comment => comment.status === 'open').length;

  return html`
    <div class="vp-editor-comments-panel">
      <header class="vp-editor-comments-head">
        <div>
          <span class="icon-[material-symbols--chat-bubble-outline]"></span>
          <div><strong>Kommentare</strong><small>${openCount} offen</small></div>
        </div>
        <button type="button" title="Kommentare aktualisieren" aria-label="Kommentare aktualisieren" @click=${() => void state.refresh(true)}>
          <span class="icon-[material-symbols--refresh]"></span>
        </button>
      </header>
      <div class="vp-editor-comment-filters" aria-label="Kommentarfilter">
        <div class="vp-editor-comment-segment">
          <button type="button" class=${state.statusFilter === 'open' ? 'is-active' : ''} @click=${() => state.setStatusFilter('open')}>Offen</button>
          <button type="button" class=${state.statusFilter === 'resolved' ? 'is-active' : ''} @click=${() => state.setStatusFilter('resolved')}>Erledigt</button>
        </div>
        <div class="vp-editor-comment-segment">
          <button type="button" class=${state.scope === 'page' ? 'is-active' : ''} @click=${() => state.setScope('page')}>Diese Seite</button>
          <button type="button" class=${state.scope === 'all' ? 'is-active' : ''} @click=${() => state.setScope('all')}>Alle Seiten</button>
        </div>
        ${authors.length > 1
          ? html`<label>Autor<select .value=${state.authorFilter} @change=${(event: Event) => state.setAuthorFilter((event.target as HTMLSelectElement).value)}><option value="all">Alle</option>${authors.map(author => html`<option value=${author}>${author}</option>`)}</select></label>`
          : ''}
      </div>
      ${state.error ? html`<p class="vp-editor-comment-error" role="alert">${state.error}</p>` : ''}
      ${state.isLoading
        ? html`<div class="vp-editor-comment-loading" aria-label="Kommentare werden geladen"><i></i><i></i><i></i></div>`
        : filtered.length
          ? html`<div class="vp-editor-comment-list">${filtered.map((comment, index) => {
              const active = state.activeCommentId === comment.id;
              const anchorExists =
                comment.anchor.pageKey !== currentPageKey || comment.anchor.audience !== currentAudience
                  ? true
                  : editorCommentAnchorExists(comment, anchorIds);
              return html`
                <article class="vp-editor-comment-item ${active ? 'is-active' : ''}">
                  <button
                    type="button"
                    class="vp-editor-comment-check"
                    title=${comment.status === 'open' ? 'Als erledigt markieren' : 'Wieder öffnen'}
                    aria-label=${comment.status === 'open' ? 'Als erledigt markieren' : 'Kommentar wieder öffnen'}
                    @click=${() => void state.updateComment(comment, { status: comment.status === 'open' ? 'resolved' : 'open' })}
                  ><span class=${comment.status === 'open' ? 'icon-[material-symbols--radio-button-unchecked]' : 'icon-[material-symbols--check-circle]'}></span></button>
                  <button type="button" class="vp-editor-comment-summary" @click=${() => {
                    state.selectComment(comment);
                    onNavigate(comment);
                  }}>
                    <span class="vp-editor-comment-number">${index + 1}</span>
                    <span>${comment.text}</span>
                    <small>${pageLabel(comment)} · ${timestamp(comment.updatedAt)}</small>
                  </button>
                  ${!anchorExists ? html`<span class="vp-editor-comment-orphan"><span class="icon-[material-symbols--location-off-outline]"></span> Anker fehlt</span>` : ''}
                  ${active
                    ? html`
                        <section class="vp-editor-comment-edit">
                          ${CommentEditor({ id: comment.id, body: comment.body, text: comment.text, onCancel: state.closePopover,
                            onSubmit: async body => { const result = await state.updateComment(comment, { body }); if (!result) throw new Error('Kommentar konnte nicht gespeichert werden.'); },
                          })}
                          <div>
                            <button type="button" @click=${() => state.setReanchorCommentId(comment.id)} class=${state.reanchorCommentId === comment.id ? 'is-active' : ''}>${state.reanchorCommentId === comment.id ? 'Stelle anklicken…' : 'Neu platzieren'}</button>
                            <button type="button" class="is-danger" aria-label="Kommentar löschen" @click=${() => void state.deleteComment(comment)}>Löschen</button>
                          </div>
                          <small>${comment.createdBy} · erstellt in ${comment.createdLocale.toUpperCase()}</small>
                        </section>
                      `
                    : ''}
                </article>
              `;
            })}</div>`
          : html`
              <div class="vp-editor-comment-empty">
                <span class="icon-[material-symbols--mark-chat-read-outline]"></span>
                <strong>${state.statusFilter === 'open' ? 'Alles abgearbeitet' : 'Noch nichts erledigt'}</strong>
                <p>${state.statusFilter === 'open' ? 'Klicke im Canvas an eine Stelle, um einen Änderungswunsch anzulegen.' : 'Erledigte Kommentare bleiben hier nachvollziehbar.'}</p>
              </div>
            `}
    </div>
  `;
};
